import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';
import apiClient from 'utils/apiClient';
import { useAuth } from 'pages/AuthProvider';

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString();
};

const discountLabel = (coupon) => {
  if (!coupon) return '-';
  if (coupon.discountType === 'percentage') return `${coupon.discountValue}%`;
  return Number(coupon.discountValue || 0).toFixed(2);
};

const applyOnLabel = {
  all: 'All',
  products: 'Products',
  categories: 'Categories',
  subcategories: 'Subcategories'
};

export default function CouponList() {
  const navigate = useNavigate();
  const { canAccess } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [updatingId, setUpdatingId] = useState('');
  const [actionError, setActionError] = useState('');

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (search.trim()) params.set('q', search.trim());
    if (statusFilter !== 'all') params.set('isActive', String(statusFilter === 'active'));
    return `/api/coupons/admin?${params.toString()}`;
  }, [page, rowsPerPage, search, statusFilter]);

  const { data, loading, error, mutate } = useGet(endpoint);
  const coupons = data?.coupons || [];

  const handleToggleActive = async (coupon) => {
    const id = coupon?._id || coupon?.id;
    if (!id) return;

    setActionError('');
    setUpdatingId(id);
    try {
      await apiClient.patch(`/api/coupons/admin/${id}/active`);
      await mutate();
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to update coupon status.');
    } finally {
      setUpdatingId('');
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
        <Typography variant="h4">Coupons</Typography>
        {canAccess('coupons', 'create') && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/coupons/new')}>
            Create Coupon
          </Button>
        )}
      </Stack>

      <MainCard>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            label="Search by code or name"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Stack>
      </MainCard>

      {actionError && <Alert severity="error">{actionError}</Alert>}
      {error && <Alert severity="error">Failed to load coupons.</Alert>}

      <MainCard title={`Coupon List (${data?.total || coupons.length || 0})`}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Applies To</TableCell>
                <TableCell>Valid Until</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>Loading coupons...</TableCell>
                </TableRow>
              ) : coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No coupons found.
                  </TableCell>
                </TableRow>
              ) : (
                coupons.map((coupon) => {
                  const couponId = coupon?._id || coupon?.id;
                  const isActive = Boolean(coupon?.isActive);
                  return (
                    <TableRow key={couponId} hover>
                      <TableCell>{coupon.code || '-'}</TableCell>
                      <TableCell>{coupon.name || '-'}</TableCell>
                      <TableCell>{discountLabel(coupon)}</TableCell>
                      <TableCell>{applyOnLabel[coupon.applyOn] || '-'}</TableCell>
                      <TableCell>{formatDate(coupon.validUntil)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={isActive ? 'Active' : 'Inactive'}
                          color={isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                          {canAccess('coupons', 'edit') && (
                            <Tooltip title="Edit coupon">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => navigate(`/coupons/${couponId}/edit`)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canAccess('coupons', 'edit') && (
                            <Tooltip title={isActive ? 'Deactivate coupon' : 'Activate coupon'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  disabled={updatingId === couponId}
                                  onClick={() => handleToggleActive(coupon)}
                                >
                                  <SyncIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={Number(data?.total || 0)}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </MainCard>
    </Stack>
  );
}
